import { useTranslation } from 'react-i18next';

import { useTools } from '@/services/Tools';

import { GROUP_LABEL_KEY, GROUP_ORDER } from './Tools.constants';
import { ToolsLoading } from './Tools.loading';
import { styles } from './Tools.styles';
import type { ToolGroupSectionProps, ToolRowProps } from './Tools.types';
import { countEnabled, groupTools, toolLabelKey } from './Tools.utils';

const ToolRow = ({ tool }: ToolRowProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'tools' });
	const labelKey = toolLabelKey(tool.name);
	const label = labelKey ? t(labelKey) : tool.name;

	return (
		<li className={styles.row}>
			<div className={styles.rowInfo}>
				<span className={styles.rowLabel}>{label}</span>
				<span className={styles.rowMono}>{tool.name}</span>
				{!tool.enabled && tool.gate && <span className={styles.gateHint}>{t('GATE_LABEL', { gate: tool.gate })}</span>}
			</div>
			<div className={styles.rowMeta}>
				<span className={styles.rowCalls}>{t('CALLS_TODAY', { count: tool.callsToday })}</span>
				<span className={tool.enabled ? styles.pillOn : styles.pillOff}>
					{tool.enabled ? t('STATUS_ENABLED') : t('STATUS_DISABLED')}
				</span>
			</div>
		</li>
	);
};

const ToolGroupSection = ({ group, tools }: ToolGroupSectionProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'tools' });

	return (
		<article className={styles.groupCard}>
			<h2 className={styles.groupTitle}>{t(GROUP_LABEL_KEY[group])}</h2>
			<ul className={styles.groupList}>
				{tools.map((tool) => (
					<ToolRow key={tool.name} tool={tool} />
				))}
			</ul>
		</article>
	);
};

export const Tools = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'tools' });
	const { data, isLoading, isError } = useTools();

	if (isLoading) return <ToolsLoading />;
	if (isError || !data) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>
			</main>
		);
	}

	const grouped = groupTools(data.items);
	const enabled = countEnabled(data.items);
	const groupsWithTools = GROUP_ORDER.filter((group) => grouped.has(group));

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<span className="font-medium text-default">{t('BREADCRUMB')}</span>
				</nav>
			</header>

			<main className={styles.content}>
				<header>
					<p className={styles.pageEyebrow}>{t('EYEBROW')}</p>
					<h1 className={styles.pageH1}>{t('TITLE')}</h1>
					<p className={styles.pageSub}>{t('SUBTITLE')}</p>
				</header>

				<section className={styles.groupsGrid} aria-label={t('TITLE')}>
					{groupsWithTools.map((group) => (
						<ToolGroupSection key={group} group={group} tools={grouped.get(group) ?? []} />
					))}
				</section>

				<p className={styles.summary}>{t('SUMMARY', { enabled, total: data.items.length })}</p>
			</main>
		</>
	);
};
